import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { fetchOfferList } from 'actions/OfferActions'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getOfferListKey from 'lib/offer/offerListKey'
import {
  buildLocalDestinationFlashOfferListFilters,
  LOCAL_DESTINATION_OFFER_ELIGIBILITY_TIMEOUT_MS,
} from 'home/components/OfferCarousels/localDestinationOfferCarouselConstants'
import {
  applyLocalDestinationOfferCarouselEligibilityTimeout,
  selectLocalDestinationOfferCarouselState,
} from 'home/components/OfferCarousels/localDestinationOfferCarouselModel'
import {
  buildLocalDestinationOfferCarouselViewModel,
  getCountryDestinationsWithIds,
  type DestinationInput,
  type LocalDestinationOfferCarouselFilterOption,
  type LocalDestinationOfferCarouselViewModel,
  type CountryDestination,
} from 'home/components/OfferCarousels/localDestinationOfferCarouselUtils'
import { EmptyArray } from 'lib/array/arrayUtils'

export type {
  LocalDestinationOfferCarouselFilterOption,
  LocalDestinationOfferCarouselViewModel,
  CountryDestination,
}

export type LocalDestinationOfferCarouselState = LocalDestinationOfferCarouselViewModel & {
  countryDestinationsWithIds: Array<CountryDestination>
  checkingEligibility: boolean
  allChecksComplete: boolean
}

export type UseLocalDestinationOfferCarouselOptions = {
  /**
   * When false, skip eligibility fetches and timeout tracking.
   * Used to keep this work off the critical path until the module is near the viewport.
   */
  enabled?: boolean
}

const MAX_ELIGIBILITY_FETCH_RETRIES = 2

export function useLocalDestinationOfferCarousel(
  destinationsInput: Array<DestinationInput>,
  options: UseLocalDestinationOfferCarouselOptions = {},
): LocalDestinationOfferCarouselState {
  const { enabled = true } = options
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [eligibilityTimedOut, setEligibilityTimedOut] = useState(false)
  const eligibilityRetryCountsRef = useRef<Record<string, number>>({})
  const [eligibilityExhaustedKeys, setEligibilityExhaustedKeys] = useState<Set<string>>(() => new Set())
  const [eligibilityRetryEpoch, setEligibilityRetryEpoch] = useState(0)
  const frozenEligibleDestinationsRef = useRef<Array<CountryDestination> | undefined>(undefined)
  const [frozenEligibleDestinations, setFrozenEligibleDestinations] = useState<
    Array<CountryDestination> | undefined
  >()

  const destinationsWithIds = useMemo(() => {
    return getCountryDestinationsWithIds(destinationsInput)
  }, [destinationsInput])

  const destinationIdsKey = destinationsWithIds
    .map(destination => destination.destinationId)
    .join('|')

  const destinationFilters = useMemo(() => {
    return destinationsWithIds.map(destination => buildLocalDestinationFlashOfferListFilters(destination.destinationId))
  }, [destinationsWithIds])

  const listKeys = useMemo(() => {
    return destinationFilters.map(filters => getOfferListKey(filters))
  }, [destinationFilters])

  useEffect(() => {
    setEligibilityTimedOut(false)
    eligibilityRetryCountsRef.current = {}
    setEligibilityExhaustedKeys(new Set())
    setEligibilityRetryEpoch(0)
    frozenEligibleDestinationsRef.current = undefined
    setFrozenEligibleDestinations(undefined)
  }, [destinationIdsKey])

  useEffect(() => {
    if (!enabled || !destinationIdsKey) {
      return
    }

    const timer = setTimeout(() => {
      setEligibilityTimedOut(true)
    }, LOCAL_DESTINATION_OFFER_ELIGIBILITY_TIMEOUT_MS)

    return () => clearTimeout(timer)
  }, [destinationIdsKey, enabled])

  useEffect(() => {
    if (!enabled) {
      return
    }

    destinationFilters.forEach(filters => {
      dispatch(fetchOfferList(filters))
    })
  }, [destinationFilters, dispatch, enabled])

  const offerListStates = useAppSelector((state) => {
    return listKeys.map(key => state.offer.offerLists[key])
  }, shallowEqual)

  useEffect(() => {
    if (!enabled) {
      return
    }

    destinationFilters.forEach((filters, index) => {
      const list = offerListStates[index]
      const listKey = listKeys[index]
      if (!list?.error || list.fetching || !listKey) {
        return
      }

      const retryCount = eligibilityRetryCountsRef.current[listKey] ?? 0
      if (retryCount >= MAX_ELIGIBILITY_FETCH_RETRIES) {
        setEligibilityExhaustedKeys((current) => {
          if (current.has(listKey)) {
            return current
          }

          const next = new Set(current)
          next.add(listKey)
          return next
        })
        return
      }

      eligibilityRetryCountsRef.current[listKey] = retryCount + 1
      dispatch(fetchOfferList(filters))
      setEligibilityRetryEpoch(current => current + 1)
    })
  }, [destinationFilters, dispatch, enabled, eligibilityRetryEpoch, listKeys, offerListStates])

  const eligibilityState = useMemo(() => {
    if (!enabled) {
      return {
        eligibleDestinations: [],
        checkingEligibility: false,
        allChecksComplete: false,
      }
    }

    const state = selectLocalDestinationOfferCarouselState(destinationsWithIds, offerListStates, {
      listKeys,
      exhaustedListKeys: eligibilityExhaustedKeys,
    })
    return applyLocalDestinationOfferCarouselEligibilityTimeout(state, eligibilityTimedOut)
  }, [destinationsWithIds, eligibilityExhaustedKeys, eligibilityTimedOut, enabled, listKeys, offerListStates])

  const { eligibleDestinations, checkingEligibility, allChecksComplete } = eligibilityState

  useEffect(() => {
    if (!enabled || frozenEligibleDestinationsRef.current !== undefined) {
      return
    }

    if (allChecksComplete || eligibilityTimedOut) {
      frozenEligibleDestinationsRef.current = eligibleDestinations
      setFrozenEligibleDestinations(eligibleDestinations)
    }
  }, [allChecksComplete, eligibleDestinations, eligibilityTimedOut, enabled])

  const settledDestinations = useMemo(() => {
    if (frozenEligibleDestinations) {
      return frozenEligibleDestinations
    }

    if (!checkingEligibility) {
      return eligibleDestinations
    }

    return EmptyArray as Array<CountryDestination>
  }, [checkingEligibility, eligibleDestinations, frozenEligibleDestinations])

  const viewModel = useMemo(() => {
    return buildLocalDestinationOfferCarouselViewModel(intl, settledDestinations)
  }, [intl, settledDestinations])

  return useMemo(() => ({
    countryDestinationsWithIds: destinationsWithIds,
    destinations: viewModel.destinations,
    destinationFilterOptions: viewModel.destinationFilterOptions,
    defaultDestinationId: viewModel.defaultDestinationId,
    checkingEligibility,
    allChecksComplete,
  }), [
    allChecksComplete,
    checkingEligibility,
    destinationsWithIds,
    viewModel.defaultDestinationId,
    viewModel.destinationFilterOptions,
    viewModel.destinations,
  ])
}
